import { Link } from '@tanstack/react-router'
import { ChevronRight, Home, Building2, FolderOpen } from 'lucide-react'

import { useDocument, useKnowledgeBases } from '@/hooks'
import { cn } from '@/lib/utils'

interface BreadcrumbItem {
  id: string
  title: string
  href: string
  icon?: React.ReactNode
}

interface DocumentBreadcrumbProps {
  documentId: string
  className?: string
}

/**
 * 文档面包屑导航组件
 * 显示从知识库到当前文档的完整路径
 */
export function DocumentBreadcrumb({ documentId, className }: DocumentBreadcrumbProps) {
  const { data: document } = useDocument(documentId)
  const { data: knowledgeBases } = useKnowledgeBases()

  const knowledgeBase = knowledgeBases?.find((kb) => kb.id === document?.knowledgeBaseId)

  if (!document || !knowledgeBase) {
    return null
  }

  // Build breadcrumb path
  const items: BreadcrumbItem[] = [
    {
      id: 'home',
      title: '知识库',
      href: '/knowledge-base',
      icon: <Home className="h-4 w-4" />,
    },
    {
      id: knowledgeBase.id,
      title: knowledgeBase.name,
      href: `/knowledge-base/spaces/${knowledgeBase.id}`,
      icon: knowledgeBase.type === 'company' ? (
        <Building2 className="h-4 w-4" />
      ) : (
        <FolderOpen className="h-4 w-4" />
      ),
    },
  ]

  // Add current document
  items.push({
    id: document.id,
    title: document.title,
    href: `/knowledge-base/documents/${document.id}`,
  })

  return (
    <nav className={cn('flex items-center text-sm', className)} aria-label="面包屑导航">
      <ol className="flex items-center gap-1">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            )}
            {index === items.length - 1 ? (
              <span className="font-medium text-foreground truncate max-w-[200px]">
                {item.title}
              </span>
            ) : (
              /* @ts-expect-error - TanStack Router type inference issue with dynamic paths */
              <Link
                to={item.href}
                className="text-muted-foreground transition-colors hover:text-foreground flex items-center gap-1"
              >
                {item.icon ? (
                  item.icon
                ) : (
                  <span className="max-w-[150px] truncate">{item.title}</span>
                )}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

interface KnowledgeBaseBreadcrumbProps {
  knowledgeBaseId: string
  className?: string
}

/**
 * 知识库面包屑导航组件
 */
export function KnowledgeBaseBreadcrumb({ knowledgeBaseId, className }: KnowledgeBaseBreadcrumbProps) {
  const { data: knowledgeBases } = useKnowledgeBases()
  const knowledgeBase = knowledgeBases?.find((kb) => kb.id === knowledgeBaseId)

  if (!knowledgeBase) {
    return null
  }

  return (
    <nav className={cn('flex items-center text-sm', className)} aria-label="面包屑导航">
      <ol className="flex items-center gap-1">
        <li className="flex items-center gap-1">
          <Link
            to="/knowledge-base"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>
        <li className="flex items-center gap-1">
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="font-medium text-foreground flex items-center gap-1">
            {knowledgeBase.type === 'company' ? (
              <Building2 className="h-4 w-4" />
            ) : (
              <FolderOpen className="h-4 w-4" />
            )}
            {knowledgeBase.name}
          </span>
        </li>
      </ol>
    </nav>
  )
}

// Legacy export for backward compatibility
export { KnowledgeBaseBreadcrumb as SpaceBreadcrumb }
